import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { ContributorDialog } from '@rhg/components/contributor/dialog';
import { ContributorListItem } from '@rhg/components/contributor/list-item';
import {
    ContributorSelection,
    ContributorType,
} from '@rhg/components/contributor/types';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import { useProgramActions } from '@rhg/hooks/use-program-actions';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import {
    SxProps,
    Box,
    Divider,
    Icon,
    Paper,
    Theme,
    Typography,
    Grid,
} from '@rhg/libs/material';
import { TimeOutline, TotalSessionsOutline } from '@rhg/libs/material/icons';
import NextImage from '@rhg/libs/next/image';
import { getProgramSessionDurationRange } from '@rhg/utils/programs';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ProgramNextButton } from '../next-button';

const styleDivider = { width: '100%', my: DS_SPACING_UNITS.CONTENT };
const styleNextButton = { width: '100%' };
const styleSummaryContainer = {
    mt: 4,
    mb: 14,
};
const styleWhatYoullGet: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    p: DS_SPACING_UNITS.CONTENT,
    mb: DS_SPACING_UNITS.CONTENT,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.background.paper
            : theme.palette.mask.black['20p'],
};
const styleProgramStats = {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
};
const styleProgramStat = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    flexGrow: 1,
};
const styleProgramStatIcon = { color: 'text.sub' };

export function ProgramOverview() {
    const { program, programType, numSessions } = useProvidedProgramContext();
    const { handleNextButton } = useProgramActions();

    const [selectedContributor, setSelectedContributor] =
        useState<ContributorSelection>();

    const handleAuthorClick = useCallback(
        () =>
            setSelectedContributor({
                contributor: program!.author!,
                contributorType: ContributorType.Author,
            }),
        [program, setSelectedContributor]
    );

    const handleNarratorClick = useCallback(
        () =>
            setSelectedContributor({
                contributor: program!.narrator!,
                contributorType: ContributorType.Narrator,
            }),
        [program, setSelectedContributor]
    );

    const { formatMessage } = useIntl();

    const categoryIcon = useMemo(() => {
        return programType === 'clinical_program' ? (
            <NextImage
                src="/_n/images/ch-grad.png"
                alt=""
                width={64}
                height={64}
            />
        ) : undefined;
    }, [programType]);

    const { max, min } = useMemo(() => {
        const { max, min } = program
            ? getProgramSessionDurationRange(program)
            : { max: 0, min: 0 };
        return {
            max: Math.round(max / 60 / 1000),
            min: Math.round(min / 60 / 1000),
        };
    }, [program]);

    const commonTitleKey =
        programType === 'clinical_program'
            ? 'common.program_type.clinical_program.title'
            : 'common.program_type.undefined.title';
    const commonDescriptionKey =
        programType === 'clinical_program'
            ? 'common.program_type.clinical_program.description'
            : 'common.program_type.undefined.description';

    return (
        <Grid container justifyContent="center">
            <Grid item xs={12}>
                {program?.narrator && (
                    <ContributorListItem
                        clickable
                        trackButton="ViewProgramNarrator"
                        onClick={handleNarratorClick}
                        contributor={program?.narrator}
                        contributorType={ContributorType.Narrator}
                    />
                )}
                {program?.author && (
                    <ContributorListItem
                        trackButton="ViewProgramAuthor"
                        onClick={handleAuthorClick}
                        clickable
                        contributor={program?.author}
                        contributorType={ContributorType.Author}
                    />
                )}
            </Grid>
            <Divider aria-hidden orientation="horizontal" sx={styleDivider} />
            <MaxColumnsContainer columns={6}>
                <ProgramNextButton
                    trackButton="ProgramOverviewNext"
                    sx={styleNextButton}
                    onClick={handleNextButton()}
                />
            </MaxColumnsContainer>
            <Divider aria-hidden orientation="horizontal" sx={styleDivider} />
            <Grid item xs={12} sx={styleSummaryContainer}>
                <Typography
                    variant="h5"
                    component="h2"
                    color="text.body"
                    mb={1}
                >
                    {formatMessage(commonMessages['common.summary'])}
                </Typography>
                <Typography variant="subtitle1" color="text.sub" component="p">
                    {program?.description}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography
                    textAlign="left"
                    variant="h5"
                    component="h2"
                    color="text.body"
                    mb={8}
                >
                    {formatMessage(programMessages['program.what_youll_get'])}
                </Typography>
            </Grid>
            <Grid item xs={12} mb={4}>
                <Paper sx={styleWhatYoullGet}>
                    <Box mb={2}>{categoryIcon}</Box>
                    <Typography
                        color="primary"
                        variant="body1Demi"
                        component="h3"
                        mb={1}
                    >
                        {formatMessage(commonMessages[commonTitleKey])}
                    </Typography>
                    <Typography
                        textAlign="center"
                        color="text.sub"
                        variant="subtitle2"
                        component="p"
                    >
                        {formatMessage(commonMessages[commonDescriptionKey])}
                    </Typography>
                    <Divider
                        aria-hidden
                        orientation="horizontal"
                        sx={styleDivider}
                    />
                    <MaxColumnsContainer columns={4}>
                        <Box sx={styleProgramStats}>
                            <Box
                                sx={styleProgramStat}
                                role="group"
                                aria-label={`${formatMessage(
                                    commonMessages['common.total_sessions']
                                )}: ${numSessions}`}
                            >
                                <Icon sx={styleProgramStatIcon}>
                                    <TotalSessionsOutline />
                                </Icon>
                                <Typography
                                    mb={2}
                                    mt={1}
                                    variant="caption"
                                    color="text.body"
                                    aria-hidden
                                >
                                    {formatMessage(
                                        commonMessages['common.total_sessions']
                                    )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.body"
                                    aria-hidden
                                >
                                    {numSessions}
                                </Typography>
                            </Box>
                            <Box
                                sx={styleProgramStat}
                                role="group"
                                aria-label={`${formatMessage(
                                    commonMessages['common.session_minutes']
                                )}: ${formatMessage(
                                    commonMessages[
                                        'common.session_minutes_description'
                                    ],
                                    {
                                        min,
                                        max,
                                    }
                                )}`}
                            >
                                <Icon sx={styleProgramStatIcon}>
                                    <TimeOutline />
                                </Icon>
                                <Typography
                                    mb={2}
                                    mt={1}
                                    variant="caption"
                                    color="text.body"
                                    aria-hidden
                                >
                                    {formatMessage(
                                        commonMessages['common.session_minutes']
                                    )}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.body"
                                    aria-hidden
                                >
                                    {formatMessage(
                                        commonMessages[
                                            'common.session_minutes_description'
                                        ],
                                        {
                                            min,
                                            max,
                                        }
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </MaxColumnsContainer>
                </Paper>
            </Grid>
            <Typography color="text.sub" variant="subtitle2" component="p">
                {formatMessage(commonMessages['common.clinical_disclaimer'])}
            </Typography>
            <ContributorDialog
                isOpen={!!selectedContributor}
                contributor={selectedContributor?.contributor}
                contributorType={selectedContributor?.contributorType}
                onClose={() => setSelectedContributor(undefined)}
            />
        </Grid>
    );
}
